import { useState, useEffect, useRef } from 'react';
import { styled, alpha } from '@mui/material/styles';
import {
  Input,
  Slide,
  Button,
  IconButton,
  InputAdornment,
  ClickAwayListener,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Iconify from '../../components/Iconify';

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const SearchbarStyle = styled('div')(({ theme }) => ({
  top: 0,
  left: 0,
  zIndex: 99,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  height: APPBAR_MOBILE,
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  padding: theme.spacing(0, 3),
  boxShadow: theme.customShadows.z8,
  backgroundColor: `${alpha(theme.palette.background.default, 0.72)}`,
  [theme.breakpoints.up('md')]: {
    height: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

export default function Searchbar() {
  const [isOpen, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const handleSearch = async (term = query) => {
    if (!term.trim()) return;
    setLoading(true);

    try {
      const res = await fetch(`/search?q=${encodeURIComponent(term)}`);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Search API error:', errorText);
        throw new Error(`Search failed with status ${res.status}`);
      }
      const data = await res.json();
      setResults(data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result) => {
    if (result.type === 'legal_matter') {
      navigate(`/matter/${result.id}`);
    } else if (result.type === 'estate') {
      navigate(`/matter/${result.id}`);
    } else if (result.type === 'post') {
      navigate(`/blog/${result.slug || result.id}`);
    }
    setResults([]);
    setOpen(false);
    setQuery('');
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      handleSearch(text);
    }, 500);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box>
        {!isOpen && (
          <IconButton onClick={handleOpen}>
            <Iconify icon="eva:search-fill" width={20} height={20} />
          </IconButton>
        )}

        <Slide direction="down" in={isOpen} mountOnEnter unmountOnExit>
          <SearchbarStyle>
            <Input
              autoFocus
              fullWidth
              disableUnderline
              placeholder="Search for a matter, estate, or blog..."
              value={query}
              onChange={handleInputChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              startAdornment={
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                </InputAdornment>
              }
              sx={{ mr: 1, fontWeight: 'fontWeightBold' }}
            />
            <Button variant="contained" onClick={() => handleSearch()} disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Search'}
            </Button>
          </SearchbarStyle>
        </Slide>

        {results.length > 0 && (
          <Box sx={{
            position: 'absolute',
            top: APPBAR_MOBILE,
            left: 0,
            width: '100%',
            backgroundColor: 'background.paper',
            boxShadow: 2,
            zIndex: 98,
            maxHeight: 400,
            overflowY: 'auto',
            px: 3,
            py: 2,
          }}>
            <List>
              {results.map((result) => {
                let displayTitle = result.title || result.name1 || result.estate_name || 'Untitled';
                const prettyType = result.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

                return (
                  <ListItem
                    key={result.id || result.slug}
                    button
                    onClick={() => handleResultClick(result)}
                  >
                    <ListItemText
                      primary={displayTitle}
                      secondary={prettyType}
                      primaryTypographyProps={{ color: 'text.primary' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {!loading && results.length === 0 && query.trim() !== '' && (
          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary">No results found.</Typography>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
}
